import type { ElementType } from 'react';
import { Zap, Target, Trophy, Compass } from 'lucide-react';

/*
 ⚠️ DIT IS DE ENIGE BRON voor het icoon en de kleur van een oefenmodus.

 Er waren er twee. `modeRegistry.ts` draagt per modus een icoon-NAAM als string
 (`icon: 'Search'`), en `LabStation` had daar zijn eigen `iconMap` + `colorMap`
 op gebouwd. Gevolg: Vrij Oefenen was een loep op de ene kaart en een kompas in
 de briefing — hetzelfde ding met twee gezichten. En omdat de KLEUR aan die
 icoonnaam hing, veranderde het icoon aanpassen stilzwijgend ook de kleur van
 de kaart.

 Beide zijn nu gesleuteld op mode-id. `ModeMetadata.icon` is daarmee niet meer
 leidend voor labkaarten; laat dat veld staan voor niet-lab-consumenten, maar
 render in het leerlab altijd via MODE_ICON.
*/

export type ModeId = 'fluency' | 'sprint' | 'challenge' | 'free';
export interface ModeTheme {
  title: string;
  short: string;
  primaryHue: string;
  gradient: string;
  bg: string;
  bgSoft: string;
  text: string;
  textDark: string;
  border: string;
  borderSoft: string;
  shadow: string;
  ring: string;
}
export const MODE_THEME: Record<ModeId, ModeTheme> = {
  fluency: {
    title: 'Snel & Zeker',
    short: 'Fluency',
    primaryHue: 'amber',
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-100',
    bgSoft: 'bg-amber-50',
    text: 'text-amber-600',
    textDark: 'text-amber-900',
    border: 'border-amber-200',
    borderSoft: 'border-amber-100',
    shadow: 'shadow-amber-200',
    ring: 'ring-amber-300'
  },
  sprint: {
    title: 'Focus Sprint',
    short: 'Sprint',
    primaryHue: 'indigo',
    gradient: 'from-indigo-400 to-purple-500',
    bg: 'bg-indigo-100',
    bgSoft: 'bg-indigo-50',
    text: 'text-indigo-600',
    textDark: 'text-indigo-900',
    border: 'border-indigo-200',
    borderSoft: 'border-indigo-100',
    shadow: 'shadow-indigo-200',
    ring: 'ring-indigo-300'
  },
  challenge: {
    title: 'Uitdaging',
    short: 'Challenge',
    primaryHue: 'violet',
    gradient: 'from-violet-400 to-fuchsia-500',
    bg: 'bg-violet-100',
    bgSoft: 'bg-violet-50',
    text: 'text-violet-600',
    textDark: 'text-violet-900',
    border: 'border-violet-200',
    borderSoft: 'border-violet-100',
    shadow: 'shadow-violet-200',
    ring: 'ring-violet-300'
  },
  free: {
    title: 'Vrij Oefenen',
    short: 'Free',
    primaryHue: 'emerald',
    gradient: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-100',
    bgSoft: 'bg-emerald-50',
    text: 'text-emerald-600',
    textDark: 'text-emerald-900',
    border: 'border-emerald-200',
    borderSoft: 'border-emerald-100',
    shadow: 'shadow-emerald-200',
    ring: 'ring-emerald-300'
  }
};

/**
 * MODE_ICON — Single source of truth for mode icons.
 * Used by MissionBriefing, ModeIntroSheet, and any other lab surface that
 * needs to render a mode glyph. Co-located with MODE_THEME so the theme
 * tokens and visual identity stay in one place.
 */
export const MODE_ICON: Record<ModeId, ElementType> = {
  fluency: Zap,
  sprint: Target,
  challenge: Trophy,
  // Kompas, niet de loep uit het register: een loep betekent in de rest van de
  // app "zoeken/filteren". Vrij Oefenen is zelf een richting kiezen.
  free: Compass
};

/** Valt terug op de vrije modus wanneer een onbekende id langskomt. */
export function getModeVisuals(modeId: string): {
  Icon: ElementType;
  theme: ModeTheme;
} {
  const key = (modeId in MODE_THEME ? modeId : 'free') as ModeId;
  return {
    Icon: MODE_ICON[key],
    theme: MODE_THEME[key]
  };
}